import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRoleContext } from '../hooks/useRoleContext';
import { useLanguage } from '../context/LanguageContext';
import { ownerAPI, walkthroughAPI, billingAPI, listingAPI } from '../lib/api';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import OwnerInquiryViewingSlots from '../components/owner/OwnerInquiryViewingSlots';
import OwnerInquiryReviewWorkspace from '../components/owner/OwnerInquiryReviewWorkspace';
import {
  OWNER_INCOMING_BOOKING_SECTION_TEST_ID,
  ownerInquiryCardTestId,
} from '../lib/ownerViewingSlotDiscoverability';
import {
  applyInquiryStatusUpdate,
  filterApprovedFollowUpInquiries,
  filterDeclinedInquiries,
  findActionableInquiryIdForListing,
  getActiveIncomingDecisionInquiry,
  getOwnerInquiryRecordId,
  getLinkedActiveCardClass,
  isIncomingDecisionInquiry,
  isListingLinkedToActiveInquiry,
  normalizeEntityId,
  OWNER_INCOMING_INQUIRY_DETAIL_TEST_ID,
  OWNER_INCOMING_QUEUE_CONTAINER_CLASS,
  ownerLinkedListingTestId,
  pickNextActiveInquiryId,
  resolveActiveListingId,
  resolveDefaultActiveInquiryId,
  shouldShowOwnerReviewWorkspace,
  sortIncomingDecisionQueue,
  sortListingsForActiveInquiry,
} from '../lib/ownerDashboardDisplay';
import {
  Building2,
  Plus,
  Eye,
  Video,
  Clock,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Loader2,
  BarChart3,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Heart,
  MessageSquare,
  RefreshCw,
  Mail,
  XCircle,
  Calendar,
  Home,
  CalendarOff,
  CalendarCheck,
  UserCheck,
  Crown,
  Zap,
  CreditCard,
  Receipt,
  DollarSign,
  ArrowUpCircle,
  PauseCircle,
  PlayCircle,
  Trash2,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { arePaymentsEnabled } from '../lib/paymentsAvailability';
import { formatDateTime, formatInquiryDate } from '../lib/bookingInquiryDisplay';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const paymentsEnabled = arePaymentsEnabled();

export default function OwnerDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const { canAccessOwner, sessionRevision } = useRoleContext();
  const { t } = useLanguage();
  const [listings, setListings] = useState([]);
  const [stats, setStats] = useState(null);
  const [walkthroughs, setWalkthroughs] = useState([]);
  const [bookingInquiries, setBookingInquiries] = useState([]);
  const [crmReport, setCrmReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [crmLoading, setCrmLoading] = useState(false);
  const [selectedListingForReminder, setSelectedListingForReminder] = useState('');
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);
  const [listingsRequiringAction, setListingsRequiringAction] = useState([]);
  const [confirmingListing, setConfirmingListing] = useState(null);
  const [respondingTo, setRespondingTo] = useState(null);
  
  // Rental status management state
  const [showRentalModal, setShowRentalModal] = useState(null);
  const [rentalForm, setRentalForm] = useState({
    is_rented: true,
    rented_until: '',
    tenant_name: '',
    notes: ''
  });
  const [updatingRentalStatus, setUpdatingRentalStatus] = useState(false);
  
  // Listing expand state
  const [showAllListings, setShowAllListings] = useState(false);

  // Pause/delete state
  const [pausingListing, setPausingListing] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null); // { id, title }
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Plan info state
  const [planInfo, setPlanInfo] = useState(null);
  
  // Billing history state
  const [billingHistory, setBillingHistory] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);
  
  // Booking analytics state
  const [bookingAnalytics, setBookingAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Tab management
  const [activeTab, setActiveTab] = useState('overview');

  // One-shot highlight for the top priority listing card
  const [highlightListingId, setHighlightListingId] = useState(null);
  const lastPulseKeyRef = useRef(null);

  // Linked inquiry ↔ listing highlight (frontend-only, not persisted)
  const [activeInquiryId, setActiveInquiryId] = useState(null);
  const [manualListingFocusId, setManualListingFocusId] = useState(null);
  const [expandedFollowUp, setExpandedFollowUp] = useState({ approved: false, declined: false });

  // Check for confirm parameter in URL
  useEffect(() => {
    const confirmId = searchParams.get('confirm');
    if (confirmId && listings.length > 0) {
      const listing = listings.find(l => l.id === confirmId);
      if (listing) {
        confirmListing(confirmId);
      }
    }
    
    // Check for subscription success from Stripe redirect (only when payments enabled)
    const subscriptionStatus = searchParams.get('subscription');
    const paymentStatus = searchParams.get('payment');
    if (paymentsEnabled && (subscriptionStatus === 'success' || paymentStatus === 'success')) {
      toast.success(t('owner.payment_success', '🎉 Payment successful! Your plan has been upgraded.'));
      // Reload plan info to reflect the change
      loadPlanInfo();
      // Clear the URL param
      navigate('/owner/dashboard', { replace: true });
    }
  }, [searchParams, listings]);

  useEffect(() => {
    if (!authLoading && !canAccessOwner) {
      navigate('/login?redirect=/owner/dashboard');
    }
  }, [authLoading, canAccessOwner, navigate]);

  useEffect(() => {
    if (!canAccessOwner) {
      return;
    }
    setBookingInquiries([]);
    setListings([]);
    setActiveInquiryId(null);
    setManualListingFocusId(null);
    setLoading(true);
    loadData();
    loadListingsRequiringAction();
    loadBookingInquiries();
    loadPlanInfo();
  }, [canAccessOwner, sessionRevision, user?.id]);

  const loadPlanInfo = async () => {
    try {
      const res = await api.get('/billing/my-plan');
      setPlanInfo(res.data);
    } catch (error) {
      console.error('Failed to load plan info:', error);
    }
  };

  const loadData = async () => {
    try {
      const [listingsRes, statsRes, walkthroughsRes] = await Promise.all([
        ownerAPI.getListings(),
        ownerAPI.getStats(),
        walkthroughAPI.getAll(),
      ]);
      setListings(listingsRes.data);
      setStats(statsRes.data);
      setWalkthroughs(walkthroughsRes.data);
      
      // Load CRM report
      loadCrmReport();
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCrmReport = async () => {
    setCrmLoading(true);
    try {
      const res = await api.get('/owner/crm-report');
      setCrmReport(res.data);
    } catch (error) {
      console.error('Failed to load CRM report:', error);
    } finally {
      setCrmLoading(false);
    }
  };

  const loadBillingHistory = async () => {
    setBillingLoading(true);
    try {
      const res = await billingAPI.getHistory();
      setBillingHistory(res.data.transactions || []);
    } catch (error) {
      console.error('Failed to load billing history:', error);
    } finally {
      setBillingLoading(false);
    }
  };

  const loadBookingAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await api.get('/owner/booking-analytics');
      setBookingAnalytics(res.data);
    } catch (error) {
      console.error('Failed to load booking analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadListingsRequiringAction = async () => {
    try {
      const res = await api.get('/owner/listings/requiring-action');
      const getMeaningfulActionTimestamp = (listing) => {
        const toTime = (value) => {
          if (!value) return 0;
          const parsed = new Date(value).getTime();
          return Number.isNaN(parsed) ? 0 : parsed;
        };

        if (listing.action_type === 'REVIEW_CHANGES_REQUIRED') {
          return Math.max(
            toTime(listing.latest_review_round_sent_at),
            toTime(listing.latest_review_batch_sent_at),
          );
        }

        if (listing.action_type === 'REACTIVATE') {
          return Math.max(
            toTime(listing.updated_at),
            toTime(listing.action_required_by),
          );
        }

        return Math.max(
          toTime(listing.action_required_by),
          toTime(listing.updated_at),
        );
      };

      const ordered = [...(res.data.listings || [])].sort(
        (a, b) => getMeaningfulActionTimestamp(b) - getMeaningfulActionTimestamp(a),
      );
      setListingsRequiringAction(ordered);
    } catch (error) {
      console.error('Failed to load listings requiring action:', error);
    }
  };

  const confirmListing = async (listingId) => {
    setConfirmingListing(listingId);
    try {
      await api.post(`/owner/listings/${listingId}/confirm`);
      toast.success('Listing confirmed as active!');
      // Refresh data
      loadData();
      loadListingsRequiringAction();
      // Clear URL param
      navigate('/owner/dashboard', { replace: true });
    } catch (error) {
      console.error('Failed to confirm listing:', error);
      toast.error('Failed to confirm listing');
    } finally {
      setConfirmingListing(null);
    }
  };

  const reactivateListing = async (listingId) => {
    setConfirmingListing(listingId);
    try {
      await api.post(`/owner/listings/${listingId}/reactivate`);
      toast.success('Listing reactivated successfully!');
      // Refresh data
      loadData();
      loadListingsRequiringAction();
    } catch (error) {
      console.error('Failed to reactivate listing:', error);
      toast.error('Failed to reactivate listing');
    } finally {
      setConfirmingListing(null);
    }
  };

  const loadBookingInquiries = async () => {
    try {
      const res = await api.get('/owner/booking-inquiries');
      setBookingInquiries(res.data.inquiries || []);
    } catch (error) {
      console.error('Failed to load booking inquiries:', error);
      toast.error(
        t('owner.incoming_inquiries_load_failed', 'Failed to load incoming booking requests'),
      );
    }
  };

  const respondToInquiry = async (inquiryId, status, message = '') => {
    setRespondingTo(inquiryId);
    const timestamp = new Date().toISOString();
    const patch = {
      status,
      owner_response: message,
      responded_at: timestamp,
      updated_at: timestamp,
    };
    try {
      await api.patch(`/owner/booking-inquiries/${inquiryId}`, {
        status,
        owner_response: message,
      });
      toast.success(`Inquiry ${status}!`);
      const updated = applyInquiryStatusUpdate(bookingInquiries, inquiryId, patch);
      setBookingInquiries(updated);
      setActiveInquiryId(pickNextActiveInquiryId(updated, { excludeId: inquiryId }));
      setManualListingFocusId(null);
      setExpandedFollowUp((prev) => ({
        ...prev,
        ...(status === 'approved' ? { approved: true } : { declined: true }),
      }));
    } catch (error) {
      console.error('Failed to respond to inquiry:', error);
      toast.error('Failed to respond to inquiry');
    } finally {
      setRespondingTo(null);
    }
  };

  const sendFunnyReminder = async () => {
    if (!selectedListingForReminder) return;
    
    setSendingReminder(true);
    setReminderResult(null);
    
    try {
      const response = await api.post('/owner/send-funny-reminder', { 
        listing_id: selectedListingForReminder 
      });
      
      const data = response.data;
      setReminderResult(data);
      if (data.sent > 0) {
        toast.success(`Funny reminder sent to ${data.sent} user(s)!`);
      } else {
        toast.info(data.message || 'No users to notify yet');
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error('Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  // Rental status management functions
  const openRentalModal = (listing, markAsRented = true) => {
    setShowRentalModal(listing);
    // Set default date to 30 days from now if marking as rented
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 30);
    
    setRentalForm({
      is_rented: markAsRented,
      rented_until: markAsRented ? defaultDate.toISOString().split('T')[0] : '',
      tenant_name: listing.rental_tenant_name || '',
      notes: listing.rental_notes || ''
    });
  };

  const updateRentalStatus = async () => {
    if (!showRentalModal) return;
    
    setUpdatingRentalStatus(true);
    try {
      await api.post(`/owner/listings/${showRentalModal.id}/rental-status`, {
        is_rented: rentalForm.is_rented,
        rented_until: rentalForm.is_rented ? rentalForm.rented_until : null,
        tenant_name: rentalForm.tenant_name || null,
        notes: rentalForm.notes || null
      });
      
      if (rentalForm.is_rented) {
        toast.success(`Property marked as rented until ${rentalForm.rented_until}`);
      } else {
        toast.success('Property marked as available');
      }
      
      setShowRentalModal(null);
      loadData();
    } catch (error) {
      console.error('Failed to update rental status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update rental status');
    } finally {
      setUpdatingRentalStatus(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDaysUntilAvailable = (rentedUntil) => {
    if (!rentedUntil) return null;
    const today = new Date();
    const endDate = new Date(rentedUntil);
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const orderedListings = useMemo(() => {
    const actionById = Object.fromEntries(
      listingsRequiringAction.map((item) => [item.id, item]),
    );

    const getMeaningfulTimestamp = (listing) => {
      const candidates = [
        listing.latest_review_round_sent_at,
        listing.latest_review_batch_sent_at,
        listing.owner_last_addressed_review_at,
        listing.last_resubmitted_at,
        listing.resubmitted_for_review_at,
        listing.submitted_for_review_at,
        listing.updated_at,
        listing.created_at,
      ];
      return Math.max(
        ...candidates.map((value) => {
          if (!value) return 0;
          const ts = new Date(value).getTime();
          return Number.isNaN(ts) ? 0 : ts;
        }),
        0,
      );
    };

    const getPriorityTier = (listing) => {
      if (listing.action_type === 'REVIEW_CHANGES_REQUIRED') return 0;
      if (
        listing.action_type === 'REACTIVATE'
        || listing.action_type === 'CONFIRM_ACTIVE'
        || actionById[listing.id]
      ) {
        return 1;
      }
      if (listing.status === 'READY_FOR_REVIEW') return 2;
      return 3;
    };

    const merged = listings.map((listing) => {
      const actionMeta = actionById[listing.id];
      return actionMeta ? { ...listing, ...actionMeta } : listing;
    });

    return [...merged].sort((a, b) => {
      const tierDiff = getPriorityTier(a) - getPriorityTier(b);
      if (tierDiff !== 0) return tierDiff;
      return getMeaningfulTimestamp(b) - getMeaningfulTimestamp(a);
    });
  }, [listings, listingsRequiringAction]);

  useEffect(() => {
    const top = orderedListings[0];
    if (!top || top.action_type !== 'REVIEW_CHANGES_REQUIRED') return undefined;

    const pulseKey = `${top.id}:${top.latest_review_batch_sent_at || top.latest_review_round_sent_at || ''}`;
    if (lastPulseKeyRef.current === pulseKey) return undefined;

    lastPulseKeyRef.current = pulseKey;
    setHighlightListingId(top.id);
    const timer = setTimeout(() => setHighlightListingId(null), 2800);
    return () => clearTimeout(timer);
  }, [orderedListings]);

  const incomingDecisionQueue = useMemo(
    () => sortIncomingDecisionQueue(bookingInquiries),
    [bookingInquiries],
  );

  const approvedFollowUpInquiries = useMemo(
    () => filterApprovedFollowUpInquiries(bookingInquiries),
    [bookingInquiries],
  );

  const declinedInquiries = useMemo(
    () => filterDeclinedInquiries(bookingInquiries),
    [bookingInquiries],
  );

  useEffect(() => {
    if (
      declinedInquiries.length > 0
      && incomingDecisionQueue.length === 0
      && approvedFollowUpInquiries.length === 0
    ) {
      setExpandedFollowUp((prev) => (prev.declined ? prev : { ...prev, declined: true }));
    }
  }, [
    declinedInquiries.length,
    incomingDecisionQueue.length,
    approvedFollowUpInquiries.length,
  ]);

  const activeIncomingInquiry = useMemo(
    () => getActiveIncomingDecisionInquiry(bookingInquiries, activeInquiryId),
    [bookingInquiries, activeInquiryId],
  );

  useEffect(() => {
    if (activeInquiryId) {
      const stillVisible = incomingDecisionQueue.some(
        (inquiry) => getOwnerInquiryRecordId(inquiry) === activeInquiryId,
      );
      if (!stillVisible) {
        setActiveInquiryId(pickNextActiveInquiryId(bookingInquiries, { excludeId: activeInquiryId }));
      }
      return;
    }
    const defaultId = resolveDefaultActiveInquiryId(bookingInquiries, isIncomingDecisionInquiry);
    if (defaultId) {
      setActiveInquiryId(defaultId);
    }
  }, [incomingDecisionQueue, activeInquiryId, bookingInquiries]);

  const activeListingId = useMemo(() => {
    const fromInquiry = resolveActiveListingId(bookingInquiries, activeInquiryId);
    return fromInquiry || normalizeEntityId(manualListingFocusId);
  }, [bookingInquiries, activeInquiryId, manualListingFocusId]);

  const displayListings = useMemo(
    () => sortListingsForActiveInquiry(orderedListings, activeListingId),
    [orderedListings, activeListingId],
  );

  const handleSelectActiveInquiry = (inquiryId) => {
    setActiveInquiryId(inquiryId);
    setManualListingFocusId(null);
  };

  const handleSelectListing = (listingId) => {
    const inquiryId = findActionableInquiryIdForListing(bookingInquiries, listingId);
    if (inquiryId) {
      setActiveInquiryId(inquiryId);
      setManualListingFocusId(null);
      return;
    }
    setActiveInquiryId(null);
    setManualListingFocusId(listingId);
  };

  const openReviewNotes = (listing) => {
    const editPath = listing.latest_review_batch_id
      ? `/owner/listings/${listing.id}/edit?reviewBatch=${listing.latest_review_batch_id}`
      : `/owner/listings/${listing.id}/edit`;
    navigate(editPath);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-ivory pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-signal-blue animate-spin" />
      </div>
    );
  }

  const handlePauseListing = async (listingId, currentStatus) => {
    const newStatus = currentStatus === 'PAUSED' ? 'READY_FOR_REVIEW' : 'PAUSED';
    setPausingListing(listingId);
    try {
      await listingAPI.setStatus(listingId, newStatus);
      setListings(prev => prev.map(l => l.id === listingId ? { ...l, status: newStatus } : l));
      toast.success(newStatus === 'PAUSED' ? 'Listing paused. It is no longer visible to the public.' : 'Listing submitted for review.');
      ownerAPI.getStats().then(res => setStats(res.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update listing status.');
    } finally {
      setPausingListing(null);
    }
  };

  const handleDeleteListing = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await listingAPI.deleteListing(deleteModal.id);
      setListings(prev => prev.filter(l => l.id !== deleteModal.id));
      toast.success('Listing deleted.');
      setDeleteModal(null);
      setDeleteConfirmText('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete listing.');
    } finally {
      setDeleting(false);
    }
  };

  const statusColors = {
    DRAFT: 'bg-slate-500/20 text-slate-600 border-slate-500/30',
    INCOMPLETE: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    READY_FOR_REVIEW: 'bg-signal-blue/20 text-signal-blue border-signal-blue/30',
    PUBLISHED: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    PAUSED: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  };

  const STATUS_LABEL_MAP = {
    DRAFT: ['owner.status.draft', 'Draft'],
    INCOMPLETE: ['owner.status.incomplete', 'Incomplete'],
    READY_FOR_REVIEW: ['owner.status.ready_for_review', 'Ready for Review'],
    PUBLISHED: ['owner.status.published', 'Published'],
    PAUSED: ['owner.status.paused', 'Paused'],
    RENTED: ['owner.status.rented', 'Rented'],
  };
  const formatListingStatus = (status) => {
    const entry = STATUS_LABEL_MAP[status];
    return entry ? t(entry[0], entry[1]) : (status?.replace('_', ' ') || '');
  };

  return (
    <div className="min-h-screen bg-ivory pt-20" data-testid="owner-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-4xl font-bold text-slate-900 mb-1 tracking-tight">
              {t('owner.dashboard', 'OWNER DASHBOARD').toUpperCase()}
            </h1>
            <div className="h-1 w-16 bg-gradient-to-r from-military-gold to-amber-400 rounded-full mb-2" />
            <p className="text-slate-600">
              {t('owner.welcome_back', 'Welcome back')}, {user?.name}
            </p>
          </div>
          <Button
            onClick={() => {
              if (planInfo && planInfo.usage?.listings_remaining === 0) {
                toast.error(t('owner.listing_limit_reached_toast', 'Listing limit reached. Please upgrade your plan.'));
                navigate('/owner/plans');
              } else {
                navigate('/owner/listings/new');
              }
            }}
            className="bg-military-gold hover:bg-military-gold/90 text-slate-950 rounded-sm"
            data-testid="new-listing-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('owner.add_new_listing', 'Add New Listing')}
          </Button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-300 mb-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'overview'
                ? 'text-military-gold border-b-2 border-military-gold -mb-px'
                : 'text-slate-600 hover:text-slate-200'
            }`}
            data-testid="tab-overview"
          >
            <Building2 className="w-4 h-4" />
            {t('owner.overview', 'Overview')}
          </button>
          <button
            onClick={() => { setActiveTab('billing'); loadBillingHistory(); }}
            className={`px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'billing'
                ? 'text-military-gold border-b-2 border-military-gold -mb-px'
                : 'text-slate-600 hover:text-slate-200'
            }`}
            data-testid="tab-billing"
          >
            <CreditCard className="w-4 h-4" />
            {t('owner.billing', 'Billing')}
          </button>
          <button
            onClick={() => { setActiveTab('analytics'); loadBookingAnalytics(); }}
            className={`px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'analytics'
                ? 'text-military-gold border-b-2 border-military-gold -mb-px'
                : 'text-slate-600 hover:text-slate-200'
            }`}
            data-testid="tab-analytics"
          >
            <BarChart3 className="w-4 h-4" />
            {t('owner.analytics', 'Analytics')}
          </button>
          {/* Batch 4: Email Templates tab removed — its endpoints exist only in the
              dormant legacy server.py, so every call 404s on the live backend.
              Porting them into app/routers/owner.py is a separate feature PR. */}
        </div>

        {/* Overview Tab Content */}
        {activeTab === 'overview' && (
          <>
            {/* Plan Info Card */}
            {planInfo && (
              <div className="mb-8 bg-gradient-to-r from-signal-blue to-blue-600 rounded-xl shadow-sm p-4 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                      <Crown className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-white/80 text-sm">{t('owner.your_plan', 'Your Plan')}</p>
                      <p className="font-heading text-xl font-bold">{planInfo.plan_id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-white/80 text-sm">{t('owner.listings_used', 'Listings Used')}</p>
                      <p className="font-heading text-lg font-bold">
                        {planInfo.usage?.active_listings || 0} / {planInfo.limits?.active_listings || 1}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-white/80 text-sm">{t('owner.photos_allowed', 'Photos/Listing')}</p>
                      <p className="font-heading text-lg font-bold">
                        {planInfo.limits?.photos_per_listing || 25}
                      </p>
                    </div>
                    {planInfo.plan_id !== 'FORTRESS' && (
                      <Link
                        to="/owner/plans"
                        className="flex items-center gap-2 bg-white/20 hover:bg-white/30 transition-colors px-4 py-2 rounded-lg text-sm font-medium"
                      >
                        <Zap className="w-4 h-4" />
                        {t('owner.request_upgrade', 'Request Upgrade')}
                      </Link>
                    )}
                  </div>
                </div>
                {planInfo.usage?.listings_remaining === 0 && planInfo.plan_id !== 'FORTRESS' && (
                  <div className="mt-3 p-2 bg-white/10 rounded-lg text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {t('owner.listing_limit_reached', "You've reached your listing limit.")} 
                    <Link to="/owner/plans" className="underline font-medium">{t('owner.upgrade_now', 'Upgrade now')}</Link>
                    {t('owner.to_add_more', ' to add more listings.')}
                  </div>
                )}
              </div>
            )}

            {/* Stats Grid */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Building2 className="w-5 h-5 text-signal-blue" />
                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="font-heading text-2xl font-bold text-slate-800">
                    {stats.total_listings}
                  </div>
                  <p className="label-tactical">{t('owner.total_listings', 'Total Listings')}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div className="font-heading text-2xl font-bold text-slate-800">
                    {stats.published_listings}
                  </div>
                  <p className="label-tactical">{t('owner.published', 'Published')}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Clock className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div className="font-heading text-2xl font-bold text-slate-800">
                    {stats.pending_reviews}
                  </div>
                  <p className="label-tactical">{t('owner.pending_review', 'Pending Review')}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Video className="w-5 h-5 text-military-gold" />
                  </div>
                  <div className="font-heading text-2xl font-bold text-slate-800">
                    {stats.pending_walkthroughs}
                  </div>
                  <p className="label-tactical">{t('owner.walkthrough_requests', 'Walkthrough Requests')}</p>
                </div>
              </div>
            )}

            {/* Action Required Alert */}
        {listingsRequiringAction.length > 0 && (
          <div className="mb-8 bg-white rounded-xl shadow-sm border border-slate-200 border-red-500/30 bg-red-500/5 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-heading text-lg font-bold text-red-400 mb-2">
                  {t('owner.action_required', 'ACTION REQUIRED').toUpperCase()}
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  {listingsRequiringAction.filter(l => l.auto_deactivated).length > 0
                    ? `${listingsRequiringAction.filter(l => l.auto_deactivated).length} ${t('owner.listings_deactivated', 'listing(s) have been deactivated due to no response.')} `
                    : ''}
                  {t('owner.confirm_listings_hint', 'Please review and confirm your listings to keep them visible to guests.')}
                </p>
                
                <div className="space-y-3">
                  {listingsRequiringAction.map(listing => (
                    <div 
                      key={listing.id} 
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        listing.action_type === 'REACTIVATE' || listing.auto_deactivated
                          ? 'bg-red-500/10 border-red-500/30' 
                          : 'bg-yellow-500/10 border-yellow-500/30'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {listing.action_type === 'REACTIVATE' || listing.auto_deactivated ? (
                          <XCircle className="w-5 h-5 text-red-400" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-yellow-400" />
                        )}
                        <div>
                          <p className="text-slate-200 font-medium">{listing.title}</p>
                          <p className="text-xs text-slate-800">
                            {listing.action_type === 'REVIEW_CHANGES_REQUIRED'
                              ? `${t('owner.action_required', 'Action Required')} / ${t('owner.review_round', 'Review Round')} ${listing.review_round || listing.review_cycle_count || 1}`
                              : listing.action_type === 'REACTIVATE' || listing.auto_deactivated
                              ? t('owner.deactivated_needs_reactivation', 'Deactivated - needs reactivation')
                              : `${t('owner.action_required_by', 'Action required by')} ${new Date(listing.action_required_by).toLocaleDateString()}`
                            }
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          if (listing.action_type === 'REVIEW_CHANGES_REQUIRED') {
                            const editPath = listing.latest_review_batch_id
                              ? `/owner/listings/${listing.id}/edit?reviewBatch=${listing.latest_review_batch_id}`
                              : `/owner/listings/${listing.id}/edit`;
                            navigate(editPath);
                            return;
                          }
                          if (listing.action_type === 'REACTIVATE' || listing.auto_deactivated) {
                            reactivateListing(listing.id);
                            return;
                          }
                          confirmListing(listing.id);
                        }}
                        disabled={confirmingListing === listing.id}
                        className={listing.action_type === 'REACTIVATE' || listing.auto_deactivated
                          ? "bg-red-500 hover:bg-red-400 text-white"
                          : "bg-yellow-500 hover:bg-yellow-400 text-slate-950"
                        }
                      >
                        {confirmingListing === listing.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : listing.action_type === 'REVIEW_CHANGES_REQUIRED' ? (
                          <>{t('owner.open_review_notes', 'Open Review Notes')}</>
                        ) : listing.action_type === 'REACTIVATE' || listing.auto_deactivated ? (
                          <>{t('owner.reactivate', 'Reactivate')}</>
                        ) : (
                          <>{t('owner.confirm_active', 'Confirm Active')}</>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Listings */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-slate-800">
                {t('owner.your_listings', 'YOUR LISTINGS').toUpperCase()}
              </h2>
              {displayListings.length > 5 && (
                <button
                  onClick={() => setShowAllListings(prev => !prev)}
                  className="text-sm text-signal-blue hover:underline"
                >
                  {showAllListings ? t('owner.show_less', 'Show Less') : t('owner.view_all', 'View All')}
                </button>
              )}
            </div>

            {displayListings.length > 0 ? (
              <div className="space-y-4">
                {(showAllListings ? displayListings : displayListings.slice(0, 5)).map((listing) => {
                  const isRented = listing.is_rented;
                  const daysUntilAvailable = getDaysUntilAvailable(listing.rented_until);
                  const isReviewAction = listing.action_type === 'REVIEW_CHANGES_REQUIRED';
                  const reviewRound = listing.review_round || listing.review_cycle_count || 1;
                  const isLinkedListing = isListingLinkedToActiveInquiry(listing.id, activeListingId);
                  
                  return (
                  <div
                    key={listing.id}
                    aria-current={isLinkedListing ? 'true' : undefined}
                    data-linked-active={isLinkedListing ? 'true' : undefined}
                    data-testid={ownerLinkedListingTestId(listing.id)}
                    onClick={() => handleSelectListing(listing.id)}
                    className={`bg-white rounded-xl shadow-sm border p-4 flex gap-4 hover:border-slate-600 transition-all duration-500 cursor-pointer ${
                      isRented ? 'border-l-4 border-l-orange-500 border-slate-200' : 'border-slate-200'
                    } ${
                      highlightListingId === listing.id
                        ? 'ring-2 ring-amber-400/90 shadow-md bg-amber-50/40'
                        : isReviewAction
                          ? 'border-amber-300/80'
                          : ''
                    } ${getLinkedActiveCardClass(isLinkedListing)}`}
                  >
                    {/* Thumbnail */}
                    <div className="w-24 h-24 bg-slate-800 rounded-sm overflow-hidden flex-shrink-0 relative">
                      {listing.hero_photo_url ? (
                        <img
                          src={listing.hero_photo_url}
                          alt={listing.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Building2 className="w-8 h-8 text-slate-600" />
                        </div>
                      )}
                      {/* Rented overlay */}
                      {isRented && (
                        <div className="absolute inset-0 bg-orange-500/20 flex items-center justify-center">
                          <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded font-bold">
                            {t('owner.status.rented', 'RENTED')}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-heading text-lg font-bold text-slate-800 truncate">
                          {listing.title}
                        </h3>
                        <div className="flex gap-1 flex-shrink-0 flex-wrap justify-end">
                          {isLinkedListing && (
                            <Badge className="bg-signal-blue/15 text-signal-blue border-signal-blue/30 border text-[10px]">
                              {t('owner.linked_listing_label', 'Linked listing')}
                            </Badge>
                          )}
                          {isReviewAction && (
                            <Badge className="bg-amber-500/20 text-amber-700 border-amber-400/50 border text-xs">
                              {t('owner.action_required', 'Action Required')} / {t('owner.review_round', 'Review Round')} {reviewRound}
                            </Badge>
                          )}
                          {isRented ? (
                            <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 border text-xs">
                              {t('owner.status.rented', 'RENTED')}
                            </Badge>
                          ) : (
                            <Badge className={`${statusColors[listing.status]} border text-xs`}>
                              {formatListingStatus(listing.status)}
                            </Badge>
                          )}
                        </div>
                      </div>
                      {isReviewAction && (
                        <p className="text-xs text-amber-700 font-medium mb-1">
                          {t('owner.action_required', 'Action Required')} / {t('owner.review_round', 'Review Round')} {reviewRound}
                        </p>
                      )}
                      <p className="text-sm text-slate-600 mb-1">
                        {listing.location_name} • {listing.bedrooms} {t('common.bed', 'bed')} • {listing.bathrooms} {t('common.bath', 'bath')}
                      </p>
                      
                      {/* Rental Status Info */}
                      {isRented && listing.rented_until && (
                        <div className="flex items-center gap-2 mb-2 text-xs">
                          <CalendarCheck className="w-3 h-3 text-orange-400" />
                          <span className="text-orange-400">
                            {t('owner.available_label', 'Available:')} {formatDate(listing.rented_until)}
                            {daysUntilAvailable !== null && (
                              <span className="text-slate-800 ml-1">
                                ({daysUntilAvailable} days)
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                      
                      {/* Completion Bar - only show if not rented */}
                      {!isRented && (
                        <div className="mb-2">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-slate-800">{t('owner.completion', 'Completion')}</span>
                            <span className={listing.completion_percent >= 100 ? 'text-emerald-400' : 'text-military-gold'}>
                              {listing.completion_percent}%
                            </span>
                          </div>
                          <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all ${
                                listing.completion_percent >= 100 ? 'bg-emerald-500' : 'bg-military-gold'
                              }`}
                              style={{ width: `${listing.completion_percent}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Admin rejection feedback */}
                      {listing.status === 'DRAFT' && listing.rejection_reason && (
                        <div className="mb-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                          <span className="font-medium">Admin feedback: </span>
                          {listing.rejection_reason}
                        </div>
                      )}

                      {/* Actions */}
                      <div
                        className="flex flex-wrap gap-2"
                        role="presentation"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {isReviewAction && (
                          <Button
                            size="sm"
                            onClick={() => openReviewNotes(listing)}
                            className="bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs h-7"
                          >
                            {t('owner.open_review_notes', 'Open Review Notes')}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/owner/listings/${listing.id}/edit`)}
                          className="text-slate-600 hover:text-slate-800 text-xs h-7"
                        >
                          {t('common.edit', 'Edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/listings/${listing.id}`)}
                          className="text-slate-600 hover:text-slate-800 text-xs h-7"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          {t('owner.preview', 'Preview')}
                        </Button>

                        {/* Rental Status Toggle */}
                        {isRented ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openRentalModal(listing, false)}
                            className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 text-xs h-7"
                          >
                            <Home className="w-3 h-3 mr-1" />
                            {t('owner.mark_available', 'Mark Available')}
                          </Button>
                        ) : listing.status === 'PUBLISHED' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openRentalModal(listing, true)}
                            className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 text-xs h-7"
                          >
                            <CalendarOff className="w-3 h-3 mr-1" />
                            {t('owner.mark_rented', 'Mark Rented')}
                          </Button>
                        )}

                        {/* Pause / Resume */}
                        {listing.status === 'PUBLISHED' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handlePauseListing(listing.id, listing.status)}
                            disabled={pausingListing === listing.id}
                            className="text-slate-500 hover:text-amber-500 hover:bg-amber-500/10 text-xs h-7"
                            title="Hide from public listings"
                          >
                            {pausingListing === listing.id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <PauseCircle className="w-3 h-3 mr-1" />
                            }
                            {t('owner.pause', 'Pause')}
                          </Button>
                        )}
                        {listing.status === 'PAUSED' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handlePauseListing(listing.id, listing.status)}
                            disabled={pausingListing === listing.id}
                            className="text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10 text-xs h-7"
                            title="Submit for review to republish"
                          >
                            {pausingListing === listing.id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <PlayCircle className="w-3 h-3 mr-1" />
                            }
                            {t('owner.resubmit', 'Resubmit')}
                          </Button>
                        )}

                        {/* Delete — only for safe statuses */}
                        {['DRAFT', 'INCOMPLETE', 'PAUSED'].includes(listing.status) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setDeleteConfirmText(''); setDeleteModal({ id: listing.id, title: listing.title }); }}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs h-7"
                            title="Delete this listing"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
                <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="font-heading text-lg font-bold text-slate-800 mb-2">
                  {t('owner.no_listings', 'No Listings Yet')}
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  {t('owner.no_listings_desc', 'Create your first listing and start connecting with military families.')}
                </p>
                <Button
                  onClick={() => navigate('/owner/listings/new')}
                  className="bg-signal-blue hover:bg-signal-blue-hover text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('owner.create_listing', 'Create Listing')}
                </Button>
              </div>
            )}
          </div>

          {/* Incoming booking requests & walkthroughs */}
          <div data-testid={OWNER_INCOMING_BOOKING_SECTION_TEST_ID}>
            <div className="mb-4">
              <h2 className="font-heading text-xl font-bold text-slate-800 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-signal-blue" />
                {t('owner.incoming_booking_requests', 'Incoming Booking Requests')}
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                {t(
                  'owner.incoming_booking_section_hint',
                  'Approve or decline pending requests below. Approved requests move to the follow-up section.',
                )}
              </p>
            </div>

            {incomingDecisionQueue.length > 0 ? (
              <div className="flex flex-col gap-4 mb-4">
                <div
                  className={OWNER_INCOMING_QUEUE_CONTAINER_CLASS}
                  data-testid="owner-incoming-decision-queue"
                  role="listbox"
                  aria-label={t('owner.incoming_booking_requests', 'Incoming Booking Requests')}
                >
                  {incomingDecisionQueue.slice(0, 8).map((inquiry) => {
                    const inquiryRecordId = getOwnerInquiryRecordId(inquiry);
                    const isActiveInquiry = inquiryRecordId === activeInquiryId;
                    return (
                      <button
                        key={inquiryRecordId}
                        type="button"
                        role="option"
                        aria-selected={isActiveInquiry}
                        aria-pressed={isActiveInquiry}
                        onClick={() => handleSelectActiveInquiry(inquiryRecordId)}
                        className={`w-full text-left bg-white rounded-xl shadow-sm border border-slate-200 p-3 border-l-4 transition-all duration-300 ${
                          isActiveInquiry ? 'border-l-signal-blue' : 'border-l-slate-300'
                        } ${getLinkedActiveCardClass(isActiveInquiry)}`}
                        data-testid={ownerInquiryCardTestId(inquiryRecordId)}
                        data-linked-active={isActiveInquiry ? 'true' : undefined}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-900 truncate" title={inquiry.listing_title}>
                              {inquiry.listing_title || t('admin.unnamed_listing', 'Unnamed listing')}
                            </p>
                            <p className="text-xs text-slate-600 mt-0.5 truncate">
                              {t('owner.inquiry_guest_label', 'Guest')}: {inquiry.user_name || t('admin.owner_unknown', 'Unknown')}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {inquiry.check_in_date || '—'}
                              {' → '}
                              {inquiry.check_out_date || '—'}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            {isActiveInquiry && (
                              <Badge className="bg-signal-blue/15 text-signal-blue border-signal-blue/30 border text-[10px]">
                                {t('owner.active_inquiry_label', 'Active request')}
                              </Badge>
                            )}
                            <Badge className="text-xs shrink-0 bg-amber-500/20 text-amber-800 border-amber-300">
                              {t('owner.inquiry_status_pending', 'Pending')}
                            </Badge>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {activeIncomingInquiry ? (
                  <div
                    className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm ${getLinkedActiveCardClass(true)}`}
                    data-testid={OWNER_INCOMING_INQUIRY_DETAIL_TEST_ID}
                    data-linked-active="true"
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          {t('owner.incoming_request_detail_title', 'Request details')}
                        </p>
                        <p className="text-lg font-semibold text-slate-900 truncate mt-1" title={activeIncomingInquiry.listing_title}>
                          {activeIncomingInquiry.listing_title || t('admin.unnamed_listing', 'Unnamed listing')}
                        </p>
                        <p className="text-sm text-slate-600 mt-0.5">
                          {t('owner.inquiry_guest_label', 'Guest')}: {activeIncomingInquiry.user_name || t('admin.owner_unknown', 'Unknown')}
                        </p>
                      </div>
                      <Badge className="bg-signal-blue/15 text-signal-blue border-signal-blue/30 border text-xs shrink-0">
                        {t('owner.active_inquiry_label', 'Active request')}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-slate-700 mb-3">
                      <div>
                        <span className="text-slate-500 block text-xs">{t('booking.check_in', 'Check-in')}</span>
                        {activeIncomingInquiry.check_in_date || '—'}
                      </div>
                      <div>
                        <span className="text-slate-500 block text-xs">{t('booking.check_out', 'Check-out')}</span>
                        {activeIncomingInquiry.check_out_date || '—'}
                      </div>
                      <div>
                        <span className="text-slate-500 block text-xs">{t('booking.guests_label', 'Guests')}</span>
                        {activeIncomingInquiry.guests != null ? activeIncomingInquiry.guests : '—'}
                      </div>
                      {activeIncomingInquiry.nights != null && (
                        <div>
                          <span className="text-slate-500 block text-xs">{t('owner.nights', 'nights')}</span>
                          {activeIncomingInquiry.nights}
                        </div>
                      )}
                    </div>

                    {activeIncomingInquiry.message && (
                      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 mb-4">
                        <p className="text-xs font-medium text-slate-500 mb-1">
                          {t('owner.inquiry_guest_message_label', 'Guest message')}
                        </p>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">
                          &quot;{activeIncomingInquiry.message}&quot;
                        </p>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => respondToInquiry(
                          getOwnerInquiryRecordId(activeIncomingInquiry),
                          'approved',
                          'Your booking request has been approved!',
                        )}
                        disabled={respondingTo === getOwnerInquiryRecordId(activeIncomingInquiry)}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-8"
                        data-testid="owner-inquiry-approve-btn"
                      >
                        {respondingTo === getOwnerInquiryRecordId(activeIncomingInquiry) ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          t('owner.approve', 'Approve')
                        )}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => respondToInquiry(
                          getOwnerInquiryRecordId(activeIncomingInquiry),
                          'declined',
                          'Sorry, these dates are not available.',
                        )}
                        disabled={respondingTo === getOwnerInquiryRecordId(activeIncomingInquiry)}
                        variant="destructive"
                        className="flex-1 text-xs h-8"
                        data-testid="owner-inquiry-decline-btn"
                      >
                        {t('owner.decline', 'Decline')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/80 p-5 flex items-center justify-center text-center">
                    <p className="text-sm text-slate-600">
                      {t('owner.incoming_select_request_hint', 'Select a pending request from the queue to review details and respond.')}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center mb-4">
                <Calendar className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                <p className="text-slate-800 text-sm font-medium">
                  {t('owner.incoming_decision_queue_empty', 'No pending requests need a decision')}
                </p>
                <p className="text-slate-600 text-xs mt-1 max-w-md mx-auto">
                  {t(
                    'owner.incoming_decision_queue_empty_desc',
                    'New guest booking requests will appear here for approve or decline.',
                  )}
                </p>
                <p className="text-slate-500 text-xs mt-2 max-w-md mx-auto">
                  {t(
                    'owner.incoming_decision_queue_empty_ownership_hint',
                    'No pending requests for listings owned by this account. Inquiries route to the listing owner at submit time.',
                  )}
                </p>
                {bookingInquiries.length > 0 && (
                  <p className="text-slate-500 text-xs mt-2 max-w-md mx-auto">
                    {t(
                      'owner.incoming_non_pending_hint',
                      'This account has booking inquiries, but none are pending. Check approved/declined sections below.',
                    )}
                  </p>
                )}
                <p className="text-slate-400 text-xs mt-2 max-w-md mx-auto">
                  {t(
                    'owner.incoming_admin_trace_hint',
                    'QA: use Admin → Operations → Inquiry routing trace to verify which owner received the guest inquiry.',
                  )}
                </p>
              </div>
            )}

            {approvedFollowUpInquiries.length > 0 && (
              <div className="mb-4 rounded-xl border border-emerald-200/80 bg-emerald-50/30">
                <button
                  type="button"
                  onClick={() => setExpandedFollowUp((prev) => ({ ...prev, approved: !prev.approved }))}
                  className="w-full cursor-pointer px-4 py-3 text-sm font-medium text-slate-800 flex items-center justify-between gap-2"
                  aria-expanded={expandedFollowUp.approved}
                >
                  <span>
                    {t('owner.incoming_approved_section_title', 'Approved — viewing follow-up')}
                    {' '}
                    ({approvedFollowUpInquiries.length})
                  </span>
                  <span className="text-slate-400">{expandedFollowUp.approved ? '▲' : '▼'}</span>
                </button>
                {expandedFollowUp.approved && (
                  <div className={`${OWNER_INCOMING_QUEUE_CONTAINER_CLASS} px-4 pb-4`}>
                    {approvedFollowUpInquiries.map((inquiry) => {
                      const inquiryRecordId = getOwnerInquiryRecordId(inquiry);
                      return (
                      <div
                        key={inquiryRecordId}
                        className="rounded-lg border border-slate-200 bg-white p-3"
                        data-testid={`owner-approved-inquiry-${inquiryRecordId}`}
                      >
                        <p className="text-sm font-medium text-slate-800 truncate" title={inquiry.listing_title}>
                          {inquiry.listing_title || t('admin.unnamed_listing', 'Unnamed listing')}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {t('owner.inquiry_guest_label', 'Guest')}: {inquiry.user_name || t('admin.owner_unknown', 'Unknown')}
                        </p>
                        <OwnerInquiryViewingSlots inquiry={inquiry} />
                        {shouldShowOwnerReviewWorkspace(inquiry) && (
                          <OwnerInquiryReviewWorkspace inquiry={inquiry} />
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {declinedInquiries.length > 0 && (
              <div className="mb-6 rounded-xl border border-slate-300 bg-slate-50/80">
                <button
                  type="button"
                  onClick={() => setExpandedFollowUp((prev) => ({ ...prev, declined: !prev.declined }))}
                  className="w-full cursor-pointer px-4 py-3 text-sm font-medium text-slate-700 flex items-center justify-between gap-2"
                  aria-expanded={expandedFollowUp.declined}
                >
                  <span className="text-left">
                    <span className="block">
                      {t('owner.incoming_declined_section_title', 'Recently declined')}
                      {' '}
                      ({declinedInquiries.length})
                    </span>
                    {!expandedFollowUp.declined && (
                      <span className="block text-xs font-normal text-slate-500 mt-0.5">
                        {t(
                          'owner.incoming_declined_section_hint',
                          'Expand to review guest, dates, and property details.',
                        )}
                      </span>
                    )}
                  </span>
                  <span className="text-slate-400 shrink-0">{expandedFollowUp.declined ? '▲' : '▼'}</span>
                </button>
                {expandedFollowUp.declined && (
                  <div className={`${OWNER_INCOMING_QUEUE_CONTAINER_CLASS} px-4 pb-4`}>
                    {declinedInquiries.slice(0, 10).map((inquiry) => {
                      const inquiryRecordId = getOwnerInquiryRecordId(inquiry);
                      const checkIn = formatInquiryDate(inquiry.check_in_date) || '—';
                      const checkOut = formatInquiryDate(inquiry.check_out_date) || '—';
                      const inquiryDate = formatDateTime(inquiry.created_at)
                        || formatDateTime(inquiry.responded_at)
                        || '—';

                      return (
                        <div
                          key={inquiryRecordId}
                          className="rounded-lg border border-slate-200 bg-white p-3"
                          data-testid={`owner-declined-inquiry-${inquiryRecordId}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p
                                className="text-sm font-medium text-slate-800 truncate"
                                title={inquiry.listing_title}
                              >
                                {inquiry.listing_title || t('admin.unnamed_listing', 'Unnamed listing')}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {t('owner.inquiry_guest_label', 'Guest')}:{' '}
                                {inquiry.user_name || t('admin.owner_unknown', 'Unknown')}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {t('booking.created_at', 'Created')}: {inquiryDate}
                              </p>
                              <p className="text-xs text-slate-500 mt-1">
                                {checkIn} → {checkOut}
                              </p>
                              {(inquiry.guests != null || inquiry.nights != null) && (
                                <p className="text-xs text-slate-500 mt-1">
                                  {inquiry.guests != null && (
                                    <>
                                      {t('booking.guests_label', 'Guests')}: {inquiry.guests}
                                    </>
                                  )}
                                  {inquiry.guests != null && inquiry.nights != null && ' · '}
                                  {inquiry.nights != null && (
                                    <>
                                      {t('owner.nights', 'nights')}: {inquiry.nights}
                                    </>
                                  )}
                                </p>
                              )}
                            </div>
                            <Badge className="text-xs shrink-0 bg-slate-200 text-slate-700 border border-slate-300">
                              {t('owner.inquiry_status_declined', 'Declined')}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">
              {t('owner.walkthrough_requests', 'WALKTHROUGH REQUESTS')}
            </h2>

            {walkthroughs.length > 0 ? (
              <div className="space-y-3">
                {walkthroughs.slice(0, 5).map((wt) => (
                  <div key={wt.id} className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium text-slate-700">
                          {wt.requester_name || t('owner.walkthrough_anonymous', 'Anonymous')}
                        </p>
                        <p className="text-xs text-slate-800 truncate">
                          {wt.listing_title}
                        </p>
                      </div>
                      <Badge className={`text-xs ${
                        wt.status === 'SUBMITTED' || wt.status === 'PENDING_OWNER'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : wt.status === 'CONFIRMED'
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-slate-500/20 text-slate-600'
                      }`}>
                        {wt.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-800">
                      <Video className="w-3 h-3" />
                      {wt.preferred_method}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center">
                <Video className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                <p className="text-slate-800 text-sm">{t('owner.no_walkthrough_requests', 'No walkthrough requests yet')}</p>
              </div>
            )}

            {/* Funny Reminder Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mt-6 border-purple-500/20 bg-purple-500/5">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="w-5 h-5 text-purple-400" />
                <span className="text-sm font-medium text-slate-700">{t('owner.send_reminder', 'Send Funny Reminder')}</span>
              </div>
              <p className="text-xs text-slate-800 mb-3">
                {t('owner.send_reminder_desc', 'Send a playful email reminder to users who favorited your listings.')}
              </p>
              
              {listings.length > 0 ? (
                <div className="space-y-2">
                  <select
                    value={selectedListingForReminder}
                    onChange={(e) => setSelectedListingForReminder(e.target.value)}
                    className="w-full text-sm bg-white border border-slate-300 rounded px-3 py-2 text-slate-800"
                  >
                    <option value="">{t('owner.select_listing', 'Select a listing...')}</option>
                    {listings.filter(l => l.status === 'PUBLISHED').map(listing => (
                      <option key={listing.id} value={listing.id}>
                        {listing.title}
                      </option>
                    ))}
                  </select>
                  
                  <Button
                    size="sm"
                    onClick={sendFunnyReminder}
                    disabled={!selectedListingForReminder || sendingReminder}
                    className="w-full bg-purple-600 hover:bg-purple-500 text-white"
                  >
                    {sendingReminder ? (
                      <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> {t('owner.sending', 'Sending...')}</>
                    ) : (
                      <><Mail className="w-3 h-3 mr-2" /> {t('owner.send_reminder_btn', 'Send Reminder')}</>
                    )}
                  </Button>
                  
                  {reminderResult && (
                    <div className={`p-2 rounded text-xs mt-2 ${
                      reminderResult.sent > 0 
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
                    }`}>
                      {reminderResult.sent > 0 
                        ? `✅ Sent to ${reminderResult.sent} user(s)!`
                        : reminderResult.message || 'No users to notify'}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-800">{t('owner.publish_first_reminder', 'Publish a listing first to use this feature.')}</p>
              )}
            </div>

            {/* Analytics Teaser */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mt-6 gold-glow">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-military-gold" />
                  <span className="text-sm font-medium text-slate-700">{t('owner.crm_report', 'Weekly CRM Report')}</span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={loadCrmReport}
                  disabled={crmLoading}
                  className="h-7 w-7 p-0"
                >
                  <RefreshCw className={`w-3 h-3 ${crmLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              
              {crmReport ? (
                <div className="space-y-3">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-slate-100 rounded p-2">
                      <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                        <Eye className="w-3 h-3" />
                        {t('owner.crm_views', 'Views (7d)')}
                      </div>
                      <div className="text-lg font-bold text-slate-800">
                        {crmReport.summary.views_7d}
                      </div>
                    </div>
                    <div className="bg-slate-100 rounded p-2">
                      <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                        <MessageSquare className="w-3 h-3" />
                        {t('owner.crm_inquiries', 'Inquiries (7d)')}
                      </div>
                      <div className="text-lg font-bold text-slate-800">
                        {crmReport.summary.inquiries_7d}
                      </div>
                    </div>
                    <div className="bg-slate-100 rounded p-2">
                      <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                        <Heart className="w-3 h-3" />
                        {t('owner.crm_total_favorites', 'Total Favorites')}
                      </div>
                      <div className="text-lg font-bold text-slate-800">
                        {crmReport.summary.favorites}
                      </div>
                    </div>
                    <div className="bg-slate-100 rounded p-2">
                      <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                        <TrendingUp className="w-3 h-3" />
                        {t('owner.crm_conversion', 'Conversion')}
                      </div>
                      <div className="text-lg font-bold text-military-gold">
                        {crmReport.summary.avg_conversion_rate}%
                      </div>
                    </div>
                  </div>
                  
                  {/* Insights */}
                  {crmReport.insights && crmReport.insights.length > 0 && (
                    <div className="space-y-2">
                      {crmReport.insights.slice(0, 2).map((insight, idx) => (
                        <div 
                          key={idx}
                          className={`p-2 rounded text-xs ${
                            insight.type === 'warning'
                              ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                              : insight.type === 'success'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-blue-50 text-blue-700 border border-blue-200'
                          }`}
                        >
                          <div className="font-medium mb-0.5">{insight.title}</div>
                          <div className="opacity-80">{insight.message}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Top Listing */}
                  {crmReport.listings && crmReport.listings.length > 0 && (
                    <div className="pt-2 border-t border-slate-200">
                      <div className="text-xs text-slate-500 mb-2">{t('owner.crm_top_performer', 'Top Performer')}</div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-700 truncate pr-2">
                          {crmReport.listings[0].title}
                        </span>
                        <span className="text-xs text-military-gold whitespace-nowrap">
                          {crmReport.listings[0].views_7d} {t('owner.crm_views_short', 'views')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-xs text-slate-800 mb-3">
                    {t('owner.crm_load_prompt', 'Get detailed analytics and KPIs for your listings.')}
                  </p>
                  <Button
                    size="sm"
                    onClick={loadCrmReport}
                    disabled={crmLoading}
                    className="w-full bg-military-gold/20 text-military-gold hover:bg-military-gold/30"
                  >
                    {crmLoading ? (
                      <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                    ) : (
                      <BarChart3 className="w-3 h-3 mr-2" />
                    )}
                    {t('owner.crm_load_btn', 'Load Report')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
          </>
        )}

        {/* Billing Tab Content */}
        {activeTab === 'billing' && (
          <div className="space-y-6" data-testid="billing-tab">
            {/* Current Plan Summary */}
            {planInfo && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="font-heading text-lg font-bold text-slate-800 flex items-center gap-2">
                      <Crown className="w-5 h-5 text-military-gold" />
                      {t('owner.current_plan', 'Current Plan')}
                    </h3>
                    <p className="text-slate-600 text-sm mt-1">
                      {t('owner.plan_details', 'Your subscription details and usage')}
                    </p>
                  </div>
                  {planInfo.plan_id !== 'FORTRESS' && (
                    <Link to="/owner/plans">
                      <Button className="bg-military-gold hover:bg-military-gold/90 text-slate-950">
                        <ArrowUpCircle className="w-4 h-4 mr-2" />
                        {t('owner.request_upgrade', 'Request Upgrade')}
                      </Button>
                    </Link>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-signal-blue/10 to-signal-blue/5 rounded-lg p-4 border border-signal-blue/20">
                    <p className="text-slate-600 text-sm">{t('owner.plan', 'Plan')}</p>
                    <p className="font-heading text-2xl font-bold text-signal-blue">{planInfo.plan_id}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <p className="text-slate-600 text-sm">{t('owner.listings_used', 'Listings Used')}</p>
                    <p className="font-heading text-2xl font-bold text-slate-800">
                      {planInfo.usage?.active_listings || 0} / {planInfo.limits?.active_listings || 1}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <p className="text-slate-600 text-sm">{t('owner.photos_per_listing', 'Photos/Listing')}</p>
                    <p className="font-heading text-2xl font-bold text-slate-800">
                      {planInfo.limits?.photos_per_listing || 25}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <p className="text-slate-600 text-sm">{t('owner.billing_cycle', 'Billing Cycle')}</p>
                    <p className="font-heading text-2xl font-bold text-slate-800">
                      {planInfo.current_period_end 
                        ? new Date(planInfo.current_period_end).toLocaleDateString()
                        : t('owner.no_billing', 'Free')
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Billing History */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-lg font-bold text-slate-800 flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-slate-600" />
                  {t('owner.billing_history', 'Billing History')}
                </h3>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={loadBillingHistory}
                  disabled={billingLoading}
                  className="border-slate-300 text-slate-600"
                >
                  {billingLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  {t('owner.refresh', 'Refresh')}
                </Button>
              </div>
              
              {billingLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-signal-blue animate-spin" />
                </div>
              ) : billingHistory.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">{t('owner.date', 'Date')}</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">{t('owner.description', 'Description')}</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">{t('owner.amount', 'Amount')}</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">{t('owner.status', 'Status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingHistory.map((transaction) => (
                        <tr key={transaction.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`transaction-${transaction.id}`}>
                          <td className="py-3 px-4 text-sm text-slate-800">
                            {new Date(transaction.created_at).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                transaction.type === 'owner_plan_upgrade' 
                                  ? 'bg-emerald-100 text-emerald-600'
                                  : 'bg-signal-blue/10 text-signal-blue'
                              }`}>
                                {transaction.type === 'owner_plan_upgrade' ? (
                                  <ArrowUpCircle className="w-4 h-4" />
                                ) : (
                                  <DollarSign className="w-4 h-4" />
                                )}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-800">
                                  {transaction.type === 'owner_plan_upgrade' 
                                    ? `Upgrade to ${transaction.plan_id}`
                                    : transaction.type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                                  }
                                </p>
                                <p className="text-xs text-slate-500">
                                  {transaction.plan_id && `${transaction.plan_id} Plan`}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm font-semibold text-slate-800">
                              €{transaction.amount?.toFixed(2) || '0.00'}
                            </span>
                            <span className="text-xs text-slate-500 ml-1">
                              {transaction.currency?.toUpperCase() || 'EUR'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <Badge 
                              variant={
                                transaction.payment_status === 'completed' || transaction.payment_status === 'paid' 
                                  ? 'success' 
                                  : transaction.payment_status === 'pending'
                                  ? 'warning'
                                  : 'secondary'
                              }
                              className={`text-xs ${
                                transaction.payment_status === 'completed' || transaction.payment_status === 'paid'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : transaction.payment_status === 'pending'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-slate-100 text-slate-600'
                              }`}
                            >
                              {transaction.payment_status === 'completed' || transaction.payment_status === 'paid' 
                                ? t('owner.paid', 'Paid')
                                : transaction.payment_status === 'pending'
                                ? t('owner.pending', 'Pending')
                                : transaction.payment_status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                              }
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 bg-slate-50 rounded-lg">
                  <Receipt className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium">{t('owner.no_transactions', 'No transactions yet')}</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {t('owner.no_transactions_desc', 'Your billing history will appear here once you upgrade your plan.')}
                  </p>
                  <Link to="/owner/plans" className="mt-4 inline-block">
                    <Button className="bg-signal-blue hover:bg-signal-blue-hover text-white">
                      <Zap className="w-4 h-4 mr-2" />
                      {t('owner.view_plans', 'View Plans')}
                    </Button>
                  </Link>
                </div>
              )}
            </div>
            
            {/* Payment Methods Info */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-6">
              <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-slate-600" />
                {t('owner.payment_methods', 'Payment Methods')}
              </h4>
              <p className="text-sm text-slate-600">
                {t('owner.payment_methods_desc', 'We accept credit and debit cards via Stripe.')}
              </p>
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <div className="w-10 h-6 bg-white rounded border border-slate-200 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-blue-600">VISA</span>
                  </div>
                  <div className="w-10 h-6 bg-white rounded border border-slate-200 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-red-500">MC</span>
                  </div>
                  <div className="w-10 h-6 bg-white rounded border border-slate-200 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-blue-500">AMEX</span>
                  </div>
                </div>
                <span className="text-xs text-slate-400">Secured by Stripe</span>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab Content */}
        {activeTab === 'analytics' && (
          <div className="space-y-6" data-testid="analytics-tab">
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-signal-blue animate-spin" />
              </div>
            ) : bookingAnalytics ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                    <p className="text-slate-500 text-xs">{t('analytics.total_bookings', 'Total Bookings')}</p>
                    <p className="text-2xl font-bold text-slate-800">{bookingAnalytics.summary?.total_bookings || 0}</p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                    <p className="text-slate-500 text-xs">{t('analytics.confirmed', 'Confirmed')}</p>
                    <p className="text-2xl font-bold text-emerald-600">{bookingAnalytics.summary?.confirmed_bookings || 0}</p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                    <p className="text-slate-500 text-xs">{t('analytics.pending', 'Pending')}</p>
                    <p className="text-2xl font-bold text-amber-600">{bookingAnalytics.summary?.pending_bookings || 0}</p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                    <p className="text-slate-500 text-xs">{t('analytics.total_revenue', 'Total Revenue')}</p>
                    <p className="text-2xl font-bold text-signal-blue">€{(bookingAnalytics.summary?.total_revenue || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                    <p className="text-slate-500 text-xs">{t('analytics.avg_value', 'Avg. Booking')}</p>
                    <p className="text-2xl font-bold text-slate-800">€{(bookingAnalytics.summary?.avg_booking_value || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                    <p className="text-slate-500 text-xs">{t('analytics.occupancy', 'Occupancy Rate')}</p>
                    <p className="text-2xl font-bold text-military-gold">{bookingAnalytics.summary?.occupancy_rate || 0}%</p>
                  </div>
                </div>

                {/* Monthly Trend */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="font-heading text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-signal-blue" />
                    {t('analytics.monthly_trend', 'Monthly Booking Trend')}
                  </h3>
                  <div className="space-y-3">
                    {bookingAnalytics.monthly_data?.map((month, idx) => (
                      <div key={idx} className="flex items-center gap-4">
                        <span className="text-sm text-slate-600 w-20">{month.month}</span>
                        <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-signal-blue to-military-gold h-full rounded-full flex items-center justify-end pr-2"
                            style={{ 
                              width: `${Math.max(5, (month.bookings / Math.max(...bookingAnalytics.monthly_data.map(m => m.bookings || 1))) * 100)}%` 
                            }}
                          >
                            <span className="text-xs text-white font-medium">{month.bookings}</span>
                          </div>
                        </div>
                        <span className="text-sm font-medium text-slate-800 w-24 text-right">€{month.revenue.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Top Listings */}
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                    <h3 className="font-heading text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                      <Crown className="w-5 h-5 text-military-gold" />
                      {t('analytics.top_listings', 'Top Performing Listings')}
                    </h3>
                    {bookingAnalytics.by_listing?.length > 0 ? (
                      <div className="space-y-3">
                        {bookingAnalytics.by_listing.map((listing, idx) => (
                          <div key={listing.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                            <div className="flex items-center gap-3">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                idx === 0 ? 'bg-military-gold text-slate-900' : 'bg-slate-200 text-slate-600'
                              }`}>{idx + 1}</span>
                              <div>
                                <p className="text-sm font-medium text-slate-800 truncate max-w-[200px]">{listing.title}</p>
                                <p className="text-xs text-slate-500">{listing.bookings} bookings</p>
                              </div>
                            </div>
                            <span className="font-bold text-signal-blue">€{listing.revenue.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm text-center py-8">{t('analytics.no_data', 'No booking data yet')}</p>
                    )}
                  </div>

                  {/* Recent Bookings */}
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                    <h3 className="font-heading text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-signal-blue" />
                      {t('analytics.recent_bookings', 'Recent Bookings')}
                    </h3>
                    {bookingAnalytics.recent_bookings?.length > 0 ? (
                      <div className="space-y-3">
                        {bookingAnalytics.recent_bookings.slice(0, 5).map((booking) => (
                          <div key={booking.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                            <div>
                              <p className="text-sm font-medium text-slate-800">{booking.guest_name}</p>
                              <p className="text-xs text-slate-500 truncate max-w-[180px]">{booking.listing_title}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-slate-800">€{(booking.amount || 0).toLocaleString()}</p>
                              <Badge variant={booking.status === 'confirmed' || booking.status === 'CONFIRMED' ? 'success' : 'secondary'} className="text-xs">
                                {booking.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm text-center py-8">{t('analytics.no_bookings', 'No bookings yet')}</p>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
                <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 font-medium">{t('analytics.no_data_title', 'No Analytics Data')}</p>
                <p className="text-sm text-slate-500 mt-1">{t('analytics.no_data_desc', 'Start receiving bookings to see your analytics here.')}</p>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Rental Status Modal */}
      {showRentalModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 w-full max-w-md p-6">
            <h3 className="font-heading text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              {rentalForm.is_rented ? (
                <>
                  <CalendarOff className="w-5 h-5 text-orange-400" />
                  {t('owner.modal_mark_rented', 'Mark Property as Rented')}
                </>
              ) : (
                <>
                  <Home className="w-5 h-5 text-emerald-400" />
                  {t('owner.modal_mark_available', 'Mark Property as Available')}
                </>
              )}
            </h3>
            
            <p className="text-slate-600 text-sm mb-4">
              <strong>{showRentalModal.title}</strong>
            </p>

            {rentalForm.is_rented ? (
              <div className="space-y-4">
                {/* Available Again Date */}
                <div>
                  <label className="block text-sm text-slate-700 mb-2">
                    {t('owner.available_again_on', 'Available Again On')} <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="date"
                    value={rentalForm.rented_until}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setRentalForm({ ...rentalForm, rented_until: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-800"
                    required
                  />
                  <p className="text-xs text-slate-800 mt-1">
                    {t('owner.when_available_hint', 'When will this property become available for new bookings?')}
                  </p>
                </div>

                {/* Tenant Name (Optional) */}
                <div>
                  <label className="block text-sm text-slate-700 mb-2">
                    {t('owner.tenant_name', 'Tenant Name')} <span className="text-slate-800">{t('owner.optional_for_records', '(optional, for your records)')}</span>
                  </label>
                  <input
                    type="text"
                    value={rentalForm.tenant_name}
                    onChange={(e) => setRentalForm({ ...rentalForm, tenant_name: e.target.value })}
                    placeholder="e.g., John Smith"
                    className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-800"
                  />
                </div>

                {/* Notes (Optional) */}
                <div>
                  <label className="block text-sm text-slate-700 mb-2">
                    {t('owner.notes', 'Notes')} <span className="text-slate-800">{t('owner.optional', '(optional)')}</span>
                  </label>
                  <textarea
                    value={rentalForm.notes}
                    onChange={(e) => setRentalForm({ ...rentalForm, notes: e.target.value })}
                    placeholder={t('owner.notes_placeholder', 'Any notes about this rental...')}
                    rows={2}
                    className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-800 resize-none"
                  />
                </div>

                {/* Info Notice */}
                <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-orange-300">
                      <p className="font-medium mb-1">{t('owner.mark_rented_info_title', 'What happens when you mark as rented:')}</p>
                      <ul className="list-disc list-inside text-orange-400/80 space-y-0.5">
                        <li>{t('owner.mark_rented_info_1', 'Property shows as "Rented" to guests')}</li>
                        <li>{t('owner.mark_rented_info_2', 'Availability date displayed publicly')}</li>
                        <li>{t('owner.mark_rented_info_3', 'No new booking inquiries accepted')}</li>
                        <li>{t('owner.mark_rented_info_4', 'Guests can add to wishlist for when available')}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-slate-300">
                    {t('owner.mark_available_confirm_pre', 'This will mark')} <strong>{showRentalModal.title}</strong> {t('owner.mark_available_confirm_post', 'as available for new bookings immediately.')}
                  </p>
                </div>
                
                {showRentalModal.rental_tenant_name && (
                  <div className="p-3 bg-slate-800/50 rounded-lg">
                    <p className="text-xs text-slate-800 mb-1">{t('owner.previous_tenant', 'Previous tenant:')}</p>
                    <p className="text-slate-300">{showRentalModal.rental_tenant_name}</p>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowRentalModal(null)}
                className="flex-1"
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={updateRentalStatus}
                disabled={updatingRentalStatus || (rentalForm.is_rented && !rentalForm.rented_until)}
                className={`flex-1 ${
                  rentalForm.is_rented
                    ? 'bg-orange-600 hover:bg-orange-500'
                    : 'bg-emerald-600 hover:bg-emerald-500'
                } text-white`}
              >
                {updatingRentalStatus ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : rentalForm.is_rented ? (
                  <CalendarOff className="w-4 h-4 mr-2" />
                ) : (
                  <Home className="w-4 h-4 mr-2" />
                )}
                {rentalForm.is_rented ? t('owner.action_mark_rented', 'Mark as Rented') : t('owner.action_mark_available', 'Mark as Available')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl border border-red-200 w-full max-w-md p-6">
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-heading text-lg font-bold text-slate-800">{t('owner.delete_listing_title', 'Delete Listing')}</h3>
              <button onClick={() => { setDeleteModal(null); setDeleteConfirmText(''); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
              {t('owner.delete_listing_warning', 'This removes the listing from your dashboard and the public site.')} <strong>{t('owner.cannot_be_undone', 'This cannot be undone.')}</strong> {t('owner.photos_not_removed', 'Photos stored externally are not immediately removed.')}
            </div>

            <p className="text-sm text-slate-700 mb-1">
              {t('owner.type_to_confirm', 'Type the listing title to confirm:')}
            </p>
            <p className="text-xs text-slate-500 font-mono bg-slate-50 border border-slate-200 rounded px-2 py-1 mb-3 break-all">
              {deleteModal.title}
            </p>
            <input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={t('owner.type_exact_title', 'Type exact title here')}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 mb-4 focus:outline-none focus:ring-2 focus:ring-red-400"
            />

            <div className="flex gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={() => { setDeleteModal(null); setDeleteConfirmText(''); }}
                disabled={deleting}
                className="text-slate-600"
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleDeleteListing}
                disabled={deleting || deleteConfirmText !== deleteModal.title}
                className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              >
                {deleting
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('owner.deleting', 'Deleting…')}</>
                  : <><Trash2 className="w-4 h-4 mr-2" />{t('owner.delete_listing_btn', 'Delete Listing')}</>
                }
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
